// =============================================================================
// MeuDriver — Página institucional /seguranca
// =============================================================================

import { Link } from 'react-router-dom'
import {
  Shield,
  CreditCard,
  CheckCircle,
  RefreshCcw,
  LifeBuoy,
  BadgeCheck,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useSEO } from '@/hooks/useSEO'

export default function SecurityPage() {
  useSEO({
    title: 'Segurança no MeuDriver',
    description:
      'Pagamento custodial, CNH verificada, reembolso garantido e suporte humano. Conheça os pilares de segurança do MeuDriver.',
  })

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-10">
        {/* Hero */}
        <header className="text-center space-y-3">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-600 text-white shadow-md">
            <Shield className="h-7 w-7" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            Segurança no MeuDriver
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Construímos a plataforma com foco em confiança. Veja como protegemos
            cada aula, cada pagamento e cada conta.
          </p>
        </header>

        {/* Pilares */}
        <section className="grid gap-4 sm:grid-cols-2">
          <PilarCard
            icon={<CreditCard className="h-5 w-5" />}
            title="Pagamento custodial"
            body="O valor pago pelo aluno fica retido pela plataforma até a aula ser concluída. Só liberamos o repasse ao instrutor após confirmação, garantindo proteção dos dois lados."
          />
          <PilarCard
            icon={<BadgeCheck className="h-5 w-5" />}
            title="CNH verificada"
            body="Todo instrutor passa por validação documental. Conferimos CNH categoria mínima, validade e regularidade junto a fontes públicas antes da aprovação."
          />
          <PilarCard
            icon={<RefreshCcw className="h-5 w-5" />}
            title="Reembolso garantido"
            body="Cancelamento com mais de 24h de antecedência: reembolso integral. Cancelamento do instrutor ou no-show: reembolso automático. Política clara em /politica-cancelamento."
          />
          <PilarCard
            icon={<LifeBuoy className="h-5 w-5" />}
            title="Suporte humano"
            body="Time de suporte disponível para abrir disputas, intermediar reembolsos e investigar denúncias. Resposta em até 48h pelo canal da plataforma."
          />
        </section>

        {/* Selo de verificação */}
        <section className="rounded-2xl bg-white border border-gray-100 shadow-sm p-6 sm:p-8 space-y-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-6 w-6 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Crachá de verificação
            </h2>
          </div>
          <p className="text-gray-600 leading-relaxed">
            Instrutores com todos os documentos validados pela nossa equipe
            recebem o selo <strong>"CNH verificada"</strong> no perfil público.
            Procure por ele antes de agendar.
          </p>
          <p className="text-sm text-gray-500">
            Suspeitou de fraude ou perfil falso?{' '}
            <Link
              to="/ajuda"
              className="text-brand-600 font-medium hover:underline"
            >
              Reporte para a equipe
            </Link>
            .
          </p>
        </section>

        {/* O que esperamos do instrutor */}
        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-900">
            O que esperamos do instrutor
          </h2>
          <ul className="list-disc pl-5 space-y-1.5 text-gray-600">
            <li>CNH válida, categoria compatível com o serviço oferecido.</li>
            <li>Veículo em condições adequadas, com manutenção em dia.</li>
            <li>Comportamento respeitoso e profissional durante a aula.</li>
            <li>Disponibilidade real no horário marcado (sem no-show).</li>
            <li>
              Conformidade com a legislação local de trânsito e prestação de
              serviço educacional.
            </li>
          </ul>
        </section>

        {/* O que esperamos do aluno */}
        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-900">
            O que esperamos do aluno
          </h2>
          <ul className="list-disc pl-5 space-y-1.5 text-gray-600">
            <li>Pontualidade no local combinado.</li>
            <li>Respeito ao instrutor e ao veículo utilizado.</li>
            <li>Comunicação por canais oficiais (chat da plataforma).</li>
            <li>Avaliação honesta após a aula.</li>
          </ul>
        </section>

        {/* CTA */}
        <div className="text-center pt-2">
          <Button asChild className="bg-brand-600 hover:bg-brand-700">
            <Link to="/instrutores">Encontrar um instrutor verificado</Link>
          </Button>
        </div>
      </div>
    </div>
  )
}

function PilarCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode
  title: string
  body: string
}) {
  return (
    <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5">
      <div className="flex items-center gap-2 mb-2">
        <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
          {icon}
        </div>
        <h3 className="font-semibold text-gray-900">{title}</h3>
      </div>
      <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
    </div>
  )
}
